/**
 * Custom Predictive Search with API Integration
 *
 * Overrides Shopify's default predictive search to use custom API
 * Falls back to Shopify's predictive search if API fails
 * 
 * DISPLAY MODES:
 * - 'products': Calls search-track API and shows only products (original behavior)
 * - 'suggestions': Calls suggest API and shows only search suggestions (new feature)
 * 
 * To switch modes, change CONFIG.displayMode below
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://private.api.searchaiengine.com/shopify/search-track',
    suggestionsEndpoint: 'https://private.api.searchaiengine.com/shopify/suggest.json',
    timeout: 3000, // 3 seconds for predictive (faster than full search)
    testMode: false,
    debug: false, // Set to true to enable console logs
    debounceDelay: 300, // milliseconds
    suggestionsLimit: 3, // Number of suggestions to fetch

    // Display mode: 'products' = products only, 'suggestions' = suggestions only
    displayMode: 'suggestions'  // Options: 'products' | 'suggestions'
  };

  /**
   * Helper logger that only logs if debug mode is on
   */
  function log(...args) {
    if (CONFIG.debug) {
      console.log(...args);
    }
  }

  // Wait for the custom element to be defined
  if (customElements.get('predictive-search')) {
    log('[CustomPredictiveSearch] Overriding Shopify predictive search');

    // Store original getSearchResults method
    const OriginalPredictiveSearch = customElements.get('predictive-search');
    const originalGetSearchResults = OriginalPredictiveSearch.prototype.getSearchResults;

    // Override the getSearchResults method
    OriginalPredictiveSearch.prototype.getSearchResults = async function (searchTerm) {
      log('[CustomPredictiveSearch] Searching for:', searchTerm);
      log('[CustomPredictiveSearch] Mode:', CONFIG.displayMode);

      // If search term is empty, close the dropdown and return
      if (!searchTerm || searchTerm.trim() === '') {
        log('[CustomPredictiveSearch] Empty query - closing dropdown');

        // Clear content immediately before closing
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = '';
        }

        this.close();
        this.querySelector('input[type="search"]').focus();
        return;
      }

      try {
        if (CONFIG.displayMode === 'suggestions') {
          // NEW MODE: Fetch only suggestions
          const suggestionsResult = await callSuggestionsAPI(searchTerm);

          // Fix race condition: ensure input hasn't changed since request started
          const currentInput = this.querySelector('input[type="search"]').value.trim();
          if (currentInput !== searchTerm.trim()) {
            log('[CustomPredictiveSearch] Stale request - input changed, discarding results');
            return;
          }

          // Check if we have valid results
          const hasSuggestions = suggestionsResult && suggestionsResult.resources &&
            suggestionsResult.resources.results &&
            suggestionsResult.resources.results.queries;

          if (hasSuggestions) {
            log('[CustomPredictiveSearch] ✓ API Success!');
            log('  - Got', suggestionsResult.resources.results.queries.length, 'suggestions');

            // Reset display in case it was hidden by input listener
            if (this.predictiveSearchResults) {
              this.predictiveSearchResults.style.display = '';
            }

            // Render suggestions only
            renderPredictiveResultsWithSuggestions(
              this.predictiveSearchResults,
              suggestionsResult,
              searchTerm
            );
            this.open();
          } else {
            // No suggestions - close dropdown but keep focus
            log('[CustomPredictiveSearch] No suggestions found - closing dropdown');
            if (this.predictiveSearchResults) {
              this.predictiveSearchResults.innerHTML = '';
            }
            this.close();
            this.querySelector('input[type="search"]').focus();
          }
        } else {
          // ORIGINAL MODE: Fetch only products
          const apiResults = await callCustomAPI(searchTerm);

          if (apiResults && apiResults.performed && apiResults.results) {
            log('[CustomPredictiveSearch] ✓ API Success! Got', apiResults.resultsCount, 'results');

            // Render original results (products only)
            renderPredictiveResults(this.predictiveSearchResults, apiResults);
            this.open();
          } else {
            throw new Error('Invalid API response');
          }
        }
      } catch (error) {
        log('[CustomPredictiveSearch] ✗ API Failed:', error.message);
        log('[CustomPredictiveSearch] Closing dropdown and keeping focus');

        // Close dropdown but keep focus (don't fallback to Shopify)
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = '';
        }
        this.close();
        this.querySelector('input[type="search"]').focus();
      }
    };

    // Override the close method to always clear content when closing
    const originalClose = OriginalPredictiveSearch.prototype.close;
    OriginalPredictiveSearch.prototype.close = function () {
      log('[CustomPredictiveSearch] Closing - clearing all content');

      // Store the search input and check if it has focus BEFORE closing
      const searchInput = this.querySelector('input[type="search"]');
      const hadFocus = searchInput && document.activeElement === searchInput;

      // Clear the results container before closing
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }

      // Call original close method
      if (originalClose) {
        originalClose.call(this);
      }

      // Only restore focus if the input had focus before closing
      // This allows normal close behavior (clicking outside) while preserving focus when clearing input
      if (hadFocus) {
        setTimeout(() => {
          searchInput.focus();
          log('[CustomPredictiveSearch] Focus restored after close');
        }, 0);
      }
    };

    // Add listener to detect when input is cleared (Immediate UI feedback)
    document.addEventListener('DOMContentLoaded', () => {
      const searchInputs = document.querySelectorAll('predictive-search input[type="search"]');
      searchInputs.forEach(input => {
        input.addEventListener('input', (e) => {
          if (e.target.value.trim() === '') {
            const predictiveSearch = e.target.closest('predictive-search');
            if (predictiveSearch) {
              log('[CustomPredictiveSearch] Input cleared - closing dropdown via listener');

              // Clear the results container
              const resultsContainer = predictiveSearch.querySelector('.predictive-search-results');
              if (resultsContainer) {
                resultsContainer.style.display = 'none';
              }
            }
          }
        });
      });
    });
  }

  /**
   * Call custom API for predictive search
   */
  function callCustomAPI(query) {
    if (CONFIG.testMode) {
      console.log('[CustomPredictiveSearch] 🧪 TEST MODE - Using static data');
      return getTestData(query);
    }

    console.log('[CustomPredictiveSearch] Calling API...');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);

    return fetch(CONFIG.apiEndpoint, {
      method: 'POST',
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': '1'
      },
      body: JSON.stringify({
        query: query,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        language: Shopify.locale || 'en',
        currency: Shopify.country || 'US',
        sortBy: 'relevance',
        filters: {},
        limit: 3 // Limit for predictive search
      }),
      signal: controller.signal
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .catch(error => {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('API timeout');
        }
        throw error;
      });
  }

  /**
   * Call suggestions API for search term autocomplete
   */
  function callSuggestionsAPI(query) {
    if (CONFIG.testMode) {
      console.log('[CustomPredictiveSearch] 🧪 TEST MODE - Using static suggestions');
      return getTestSuggestions(query);
    }

    console.log('[CustomPredictiveSearch] Calling Suggestions API...');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);

    // Build query parameters
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query',
      'resources[limit]': CONFIG.suggestionsLimit
    });

    return fetch(`${CONFIG.suggestionsEndpoint}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': '1'
      },
      signal: controller.signal
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`Suggestions API error: ${response.status}`);
        }
        return response.json();
      })
      .catch(error => {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Suggestions API timeout');
        }
        throw error;
      });
  }

  /**
   * Render predictive search results - ORIGINAL MODE (Products only)
   */
  function renderPredictiveResults(container, apiResults) {
    if (!container) return;

    const products = apiResults.results || [];
    const searchTerms = apiResults.terms || '';

    // Match Shopify's exact structure
    let html = '';

    if (products.length === 0) {
      html = `
        <div class="predictive-search-results">
          <div class="predictive-search__no-results">
            <p>No results found for "${escapeHtml(searchTerms)}"</p>
          </div>
        </div>
      `;
    } else {
      html = `
        <div class="predictive-search-results" id="predictive-search-results">
          <div class="results__group-1" style="display: none"></div>
          <div class="results__group-2">
            <div class="results results--products">
              <h3 class="h4" id="predictive-search-products">
                Products
                <span class="ai-badge">Smarter Results with AI Search</span>
              </h3>
              <ul role="listbox" aria-labelledby="predictive-search-products">
      `;
      //"Search with AI on light gray"
      /*
      html = `
        <div class="predictive-search-results" id="predictive-search-results">
          <div class="results__group-1" style="display: none"></div>
          <div class="results__group-2">
            <div class="results results--products">
              <h3 class="h4" id="predictive-search-products">
                Products <span style="color: #999; font-size: 12px; font-weight: normal; margin-left: 8px;">- Search with AI</span>
              </h3>
              <ul role="listbox" aria-labelledby="predictive-search-products">
      `;
      */

      // Limit to first 3 products for predictive search
      products.slice(0, 3).forEach(product => {
        html += renderProductListItem(product);
      });

      html += `
              </ul>
            </div>
          </div>
        </div>
      `;

      // Add "View more" button if there are more results
      if (apiResults.resultsCount > 3) {
        html += `
          <button class="results__search-btn" onclick="window.location.href='${Shopify.routes.root}search?q=${encodeURIComponent(searchTerms)}'">
            View more <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 5H12.5M12.5 5L8.5 1M12.5 5L8.5 9" stroke="currentColor"></path></svg>
          </button>
        `;
      }
    }

    container.innerHTML = html;
  }

  /**
   * Render predictive search results - NEW MODE (Suggestions only)
   * Uses same HTML structure as products mode for consistent styling
   */
  function renderPredictiveResultsWithSuggestions(container, suggestionsResults, query) {
    if (!container) return;

    const suggestions = (suggestionsResults &&
      suggestionsResults.resources &&
      suggestionsResults.resources.results &&
      suggestionsResults.resources.results.queries) || [];

    // Match Shopify's exact structure (same as products mode)
    let html = '';

    if (suggestions.length === 0) {
      //uncomment if you want to show empty results message
      /*
      html = `
        <div class="predictive-search-results">
          <div class="predictive-search__no-results">
            <p>No suggestions found</p>
          </div>
        </div>
      `;
      */
      html = '';
    } else {
      html = `
        <div class="predictive-search-results" id="predictive-search-results">
          <div class="results__group-1" style="display: none"></div>
          <div class="results__group-2">
            <div class="results results--products">
              <h3 class="h4" id="predictive-search-products">
                AI SEARCH SUGGESTIONS
                <span class="ai-badge">AI Search</span>
              </h3>
              <ul role="listbox" aria-labelledby="predictive-search-products">
      `;

      // Render suggestions as list items (matching product structure)
      suggestions.forEach(suggestion => {
        const suggestionText = suggestion.text || suggestion;
        const searchUrl = `${Shopify.routes.root}search?q=${encodeURIComponent(suggestionText)}`;

        // Highlight matching query in the suggestion
        const highlightedText = highlightQuery(suggestionText, query);

        html += `
              <li role="option">
                <a href="${searchUrl}">
                  <div class="results-products__info">
                    <span>${highlightedText}</span>
                  </div>
                </a>
              </li>
        `;
      });

      html += `
              </ul>
            </div>
          </div>
        </div>
      `;
    }

    container.innerHTML = html;
  }

  /**
   * Highlight matching query text in bold, rest in gray
   */
  function highlightQuery(text, query) {
    if (!query || !text) return escapeHtml(text);

    // Escape HTML first
    const escapedText = escapeHtml(text);
    const escapedQuery = escapeHtml(query);

    // Create regex to find the query (case insensitive)
    const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

    // Replace matching parts with bold
    const highlighted = escapedText.replace(regex, '<strong>$1</strong>');

    // Wrap the entire result in gray - bold parts will override for matching text
    return `<span style="color: #999;">${highlighted.replace(/<strong>(.*?)<\/strong>/g, '<strong style="color: #000; font-weight: 600;">$1</strong>')}</span>`;
  }

  /**
   * Render product list item (matching Shopify's exact HTML structure)
   */
  function renderProductListItem(product) {
    const productUrl = Shopify.routes.root + `products/${product.handle}`;
    const imageUrl = product.featuredImage && product.featuredImage.src
      ? product.featuredImage.src
      : (product.images && product.images.length > 0 ? product.images[0].src : '');

    // Match Shopify's exact list item structure
    let html = `
      <li role="option">
        <a href="${productUrl}">
          <div class="results-products__image grid__image-ratio">
    `;

    // Product image
    if (imageUrl) {
      // Add width parameter to image URL for responsive sizing
      const imageUrlWithWidth = imageUrl.includes('?')
        ? `${imageUrl}&width=240`
        : `${imageUrl}?width=240`;

      html += `
              <img src="${imageUrlWithWidth}"
                   alt="${escapeHtml(product.title)}"
                   width="240"
                   loading="lazy"
                   sizes="100vw">
      `;
    }

    html += `
          </div>
          <div class="results-products__info">
            <span>${escapeHtml(product.title)}</span>
          </div>
        </a>
      </li>
    `;

    return html;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Test data for development
   */
  function getTestData(query) {
    return Promise.resolve({
      "performed": true,
      "resultsCount": 2,
      "terms": query,
      "results": [
        {
          "title": "Test Product 1",
          "handle": "test-product-1",
          "price": "29.90",
          "featuredImage": {
            "src": "https://via.placeholder.com/150"
          }
        },
        {
          "title": "Test Product 2",
          "handle": "test-product-2",
          "price": "39.90",
          "featuredImage": {
            "src": "https://via.placeholder.com/150"
          }
        }
      ]
    });
  }

  console.log('[CustomPredictiveSearch] Initialized');
})();
